import { useEffect, useRef, useState } from 'react';

/**
 * Collapsed-by-default note on what the clearcut layers are derived from.
 *
 * Modelled on the basemap's own compact attribution control: a map carries its
 * sources with it rather than in a caption beside a chart, and the detail is
 * there for whoever goes looking without taxing everyone who doesn't. The long
 * form -- accumulation rule, comparability, training data -- lives in
 * Documentation; this is the pointer.
 *
 * `notes` are the per-FMU data notes for the current selection ({ key, label,
 * text, warn }), kept here rather than in the module panel: they qualify the
 * sources, so they sit with them.
 */
function MapSourcesInfo({ onOpenDocumentation = null, notes = [] }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Dismiss on outside click and on Escape, the way the attribution popover does.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="map-sources" ref={rootRef}>
      {open && (
        <div className="map-sources-panel" role="dialog" aria-label="Clearcut data sources">
          <p className="map-sources-title">Clearcut detection — sources</p>
          <dl className="map-sources-list">
            <dt>2016–2024</dt>
            <dd>Harmonized Landsat &amp; Sentinel-2 (HLS v2), 30 m — NASA Earthdata</dd>
            <dt>2025</dt>
            <dd>Planet NICFI PlanetScope, 3 m</dd>
            <dt>2015</dt>
            <dd>Landsat 8 OLI (HLS L30), 30 m — before Sentinel-2, single sensor</dd>
            <dt>2010</dt>
            <dd>Landsat 5 TM, 30 m — official harvest records only, no AI layer</dd>
            <dt>Labels</dt>
            <dd>Ontario MNRF Annual Report harvest blocks; FMU boundaries</dd>
            <dt>Model</dt>
            <dd>6-class U-Net; accumulated = seen in two consecutive years of a 5-year window</dd>
          </dl>
          {notes.length > 0 && (
            <>
              <p className="map-sources-title map-sources-title--section">Data notes</p>
              {notes.map((n) => (
                <p key={n.key} className={`map-sources-note${n.warn ? ' map-sources-note--warn' : ''}`}>
                  {n.label && <strong>{n.label}: </strong>}
                  {n.text}
                </p>
              ))}
            </>
          )}
          {onOpenDocumentation && (
            <button
              type="button"
              className="map-sources-link"
              onClick={() => { setOpen(false); onOpenDocumentation(); }}
            >
              Full methodology in Documentation →
            </button>
          )}
        </div>
      )}
      {/* A labelled pill, not another circled "i": the basemap attribution sits
          directly below with exactly that glyph, and two identical marks a few
          pixels apart read as one control repeated rather than two different
          things. The axe matches the Clearcut Detection module's own icon, so
          the button says whose sources these are before it is even opened. */}
      <button
        type="button"
        className="map-sources-btn"
        aria-expanded={open}
        aria-label="Clearcut data sources"
        title="Clearcut data sources"
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">🪓</span> Sources
        {notes.some((n) => n.warn) && <span className="map-sources-dot" title="Data notes for this selection" />}
      </button>
    </div>
  );
}

export default MapSourcesInfo;
